import { Invoice } from "@common/types/invoice";
import { InvoiceView } from "@common/types/views/invoiceViews";
import { InvoiceFilters } from "@common/contracts/invoice.contract";
import { useCallback, useContext, useMemo, useState } from "react";
import { EagerLoaded } from "@common/types/base/orm";
import { useScope } from "@state/scopeContext.lib";
import { useUpdateInvoice } from "@api/invoice.api";
import { pluralize } from "@common/helpers/string";
import { Pagination } from "@design-system/Pagination/Pagination";
import { HStack } from "@design-system/HStack/HStack";
import { Drawer } from "@design-system/Drawer/Drawer";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { CommandIslandTotal } from "@design-system/CommandIsland/CommandIslandTotal";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import classNames from "classnames";
import { useInvoicesView } from "contexts/InvoicesViewContext";
import { PaginatedInvoicesTableContext } from "./PaginatedInvoicesTable.context";
import { PaginatedInvoicesTableContextProvider } from "./PaginatedInvoicesTableContextProvider";
import { InvoicesView } from "./views/invoicesView/InvoicesView";
import { InvoiceDetails } from "./InvoiceDetails";
import { ManualInvoiceEditDrawer } from "./ManualInvoiceEditDrawer/ManualInvoiceEditDrawer";
import { InvoicesDrillDownPickerEmptyView } from "./InvoicesDrillDownPickerEmptyView";
import { InvoicesDrillDownPickerHeader } from "./InvoicesDrillDownPickerHeader";
import styles from "./InvoicesDrillDownDrawer.module.scss";

interface InvoicesDrillDownDrawerProps {
    view: InvoiceView;
    open: boolean;
    onClose: () => void;
    headerAddon?: React.ReactNode | (() => React.ReactNode);
    parentFilters?: InvoiceFilters;
    emptyView?: () => React.ReactNode;
    selectForTemplate?: boolean;
    onSelectInvoice?: (invoice: Invoice) => void;
    onCreateBlankTemplate?: () => void;
}

const InvoicesDrillDownDrawerContent: React.FC<
    InvoicesDrillDownDrawerProps
> = ({
    view,
    emptyView,
    parentFilters,
    selectForTemplate,
    onSelectInvoice,
    onCreateBlankTemplate,
}) => {
    const [selectedInvoice, setSelectedInvoice] = useState<Invoice>();
    const [editInvoice, setEditInvoice] = useState<Invoice | null>(null);
    const { workspaceId } = useScope();
    const updateInvoiceMutation = useUpdateInvoice();

    const handleUpdateMemo = useCallback(
        async (memo: string | null) => {
            if (!workspaceId || !selectedInvoice) {
                return;
            }
            await updateInvoiceMutation.mutateAsync({
                workspaceId,
                invoiceId: selectedInvoice.id,
                memo,
            });
        },
        [workspaceId, selectedInvoice, updateInvoiceMutation],
    );

    const {
        invoices,
        currentSort,
        handleSortChange,
        setPage,
        currentPage,
        totalPages,
        invoiceCount,
    } = useContext(PaginatedInvoicesTableContext);

    const { setFilters } = useInvoicesView();

    const handleInvoiceClick = useCallback((invoice: Invoice) => {
        setSelectedInvoice(invoice);
    }, []);

    const parentInvoiceSources = parentFilters?.invoiceSources;

    const handleClearPickerFilters = useCallback(() => {
        setFilters(
            parentInvoiceSources
                ? { invoiceSources: parentInvoiceSources }
                : {},
        );
    }, [parentInvoiceSources, setFilters]);

    const pickerEmptyView = useCallback(
        () => (
            <InvoicesDrillDownPickerEmptyView
                onClearFilters={handleClearPickerFilters}
                onCreateBlankTemplate={onCreateBlankTemplate}
            />
        ),
        [handleClearPickerFilters, onCreateBlankTemplate],
    );

    const footer = (
        <CommandIsland
            sticky
            className={commandIslandStyles.bulkActionsStickyFooter}
        >
            <HStack gap={8}>
                {totalPages > 1 && (
                    <Pagination
                        size="md"
                        pageCount={totalPages}
                        currentPage={currentPage}
                        onPageChange={setPage}
                    />
                )}
                <CommandIslandTotal>
                    {invoiceCount} {pluralize("invoice", invoiceCount)}
                </CommandIslandTotal>
            </HStack>
        </CommandIsland>
    );

    const invoicesTable = (
        <InvoicesView
            view={view}
            invoices={invoices ?? null}
            selectedInvoiceId={
                selectForTemplate ? undefined : selectedInvoice?.id
            }
            onInvoiceClick={selectForTemplate ? undefined : handleInvoiceClick}
            onSelectInvoice={selectForTemplate ? onSelectInvoice : undefined}
            sort={currentSort}
            onSortChange={handleSortChange}
            emptyView={
                emptyView ?? (selectForTemplate ? pickerEmptyView : undefined)
            }
            footer={footer}
            useFixedTopOffset={!selectForTemplate}
        />
    );

    if (selectForTemplate) {
        return (
            <div className={classNames(styles.content, styles.picker)}>
                <InvoicesDrillDownPickerHeader
                    lockedInvoiceSources={parentInvoiceSources}
                    onCreateBlankTemplate={onCreateBlankTemplate}
                />
                <div className={styles.tableArea}>{invoicesTable}</div>
            </div>
        );
    }

    return (
        <div className={styles.content}>
            {invoicesTable}

            {selectedInvoice && (
                <InvoiceDetails
                    showLeftBorder
                    invoice={selectedInvoice as EagerLoaded<Invoice, "lines">}
                    setInvoice={setSelectedInvoice}
                    onEdit={() => setEditInvoice(selectedInvoice)}
                    onUpdateMemo={handleUpdateMemo}
                    hideCloseButton={false}
                />
            )}

            {editInvoice && (
                <ManualInvoiceEditDrawer
                    invoices={[editInvoice]}
                    open
                    onClose={() => {
                        setEditInvoice(null);
                        setSelectedInvoice(undefined);
                    }}
                />
            )}
        </div>
    );
};

export const InvoicesDrillDownDrawer: React.FC<
    InvoicesDrillDownDrawerProps
> = ({
    open,
    headerAddon,
    onClose,
    parentFilters,
    selectForTemplate,
    ...rest
}) => {
    const [isOpen, setIsOpen] = useState(false);

    const shouldShowContent = isOpen || open;

    const header = useMemo(() => {
        if (selectForTemplate) {
            return "Select invoice";
        }
        if (typeof headerAddon === "function") {
            return headerAddon();
        }
        return headerAddon;
    }, [headerAddon, selectForTemplate]);

    return (
        <Drawer
            open={open}
            onClose={onClose}
            direction="right"
            onAnimationEnd={setIsOpen}
            headerAddonLeft={header ?? "Invoices"}
            noPadding
        >
            {shouldShowContent && (
                <PaginatedInvoicesTableContextProvider
                    overlayFilters={parentFilters}
                >
                    <InvoicesDrillDownDrawerContent
                        open={open}
                        onClose={onClose}
                        headerAddon={headerAddon}
                        parentFilters={parentFilters}
                        selectForTemplate={selectForTemplate}
                        {...rest}
                    />
                </PaginatedInvoicesTableContextProvider>
            )}
        </Drawer>
    );
};
